// Cloud-first song library hook. Source of truth is the Supabase
// `patches` table; localStorage is gone (apart from the legacy `:v1`
// key which the migration module reads once and then archives).
//
// Anonymous users see DEFAULT_SONGS read-only — Save throws with a
// message pointing at sign-in. Signed-in users see their own
// patches, ordered by updated_at desc.
//
// Mutations are optimistic: the in-memory `rows` state updates
// immediately and we refetch from cloud on error to recover.
// Favourites are stored in user_metadata so they sync across devices
// without needing a schema change to `patches`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase } from '../auth/client';
import { listMyPatches, type CloudPatchRow } from '../cloud/listMyPatches';
import { migrateLocalLibraryIfNeeded } from '../cloud/migrateLocalLibrary';
import { DEFAULT_SONGS } from '../defaults';
import { loadActiveId, saveActiveId } from '../storage';
import type { Song } from '../types';

const NEW_SONG_CODE =
  'setcpm(120 / 4)\n\nstack(\n  s("bd*4"),\n  note("a2 c3 e3 g3").s("sawtooth").lpf(1200),\n)\n';

// Per-user mapping of stable scratch ids (e.g. "learn-scratch-1") to
// cloud patch ids. Persisted in localStorage because the mapping is
// per-device by definition: two devices each maintain their own
// scratch slot. Keyed by uid so the slot doesn't collide across
// accounts on a shared browser.
const SCRATCH_MAP_KEY_PREFIX = 'strudel-harness:scratch-map:v1';

function loadScratchMap(uid: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`${SCRATCH_MAP_KEY_PREFIX}:${uid}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveScratchMap(uid: string, map: Record<string, string>): void {
  try {
    localStorage.setItem(
      `${SCRATCH_MAP_KEY_PREFIX}:${uid}`,
      JSON.stringify(map),
    );
  } catch {
    // Best effort.
  }
}

export type LibraryStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'anonymous';

type ImportResult = { added: number; updated: number };

export type UseLibraryResult = {
  songs: Song[];
  activeId: string | null;
  activeSong: Song | null;
  select: (id: string) => void;
  save: (title: string, code: string) => void;
  deleteSong: (id: string) => void;
  prepareNew: () => { title: string; code: string };
  importSongs: (incoming: unknown) => ImportResult | null;
  resetToDefaults: () => Song | null;
  clearAll: () => void;
  setSongCloudPatchId: (songId: string, cloudPatchId: string) => void;
  insertSong: (title: string, code: string) => string;
  upsertScratch: (id: string, title: string, code: string) => void;
  toggleFavourite: (id: string) => void;
  status: LibraryStatus;
  error: string | null;
  refetch: () => Promise<void>;
};

type FavouriteSet = Set<string>;

function readFavourites(session: Session | null): FavouriteSet {
  const raw = session?.user?.user_metadata?.favourite_song_ids;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((v): v is string => typeof v === 'string'));
}

async function writeFavourites(ids: FavouriteSet): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('auth not configured');
  const { error } = await supabase.auth.updateUser({
    data: { favourite_song_ids: Array.from(ids) },
  });
  if (error) throw new Error(`favourite update failed: ${error.message}`);
}

export function useLibrary(params: {
  session: Session | null;
  sessionLoading: boolean;
}): UseLibraryResult {
  const { session, sessionLoading } = params;
  const userId = session?.user?.id ?? null;

  const [rows, setRows] = useState<CloudPatchRow[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(() => loadActiveId());
  const setActiveId = useCallback(
    (next: string | null | ((prev: string | null) => string | null)) => {
      setActiveIdState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        saveActiveId(value);
        return value;
      });
    },
    [],
  );
  const [status, setStatus] = useState<LibraryStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [favourites, setFavourites] = useState<FavouriteSet>(() => readFavourites(session));

  const requestSeqRef = useRef(0);
  const migrationRef = useRef<{ userId: string; promise: Promise<unknown> } | null>(null);

  const anonymous = !sessionLoading && !userId;

  // Sync favourites from session metadata when it changes (after
  // updateUser, the SDK refreshes the session and our subscription
  // surfaces the new metadata).
  useEffect(() => {
    setFavourites(readFavourites(session));
  }, [session]);

  const refetch = useCallback(async () => {
    if (sessionLoading) {
      setStatus('loading');
      return;
    }
    if (!userId) {
      setRows([]);
      setStatus('anonymous');
      setError(null);
      return;
    }
    const seq = ++requestSeqRef.current;
    setStatus('loading');
    setError(null);
    try {
      // Run migration once per (user, device) before the first fetch.
      // The migration resolves to {alreadyDone: true} on subsequent
      // boots so the cost is one localStorage read.
      if (!migrationRef.current || migrationRef.current.userId !== userId) {
        migrationRef.current = {
          userId,
          promise: migrateLocalLibraryIfNeeded(userId).catch((e: unknown) => {
            // Migration failure is non-fatal: surface as the library's
            // error state but still try to render whatever cloud data
            // we can. The user's local backup is preserved under
            // `:v1:migrated-backup` regardless.
            console.error('[library] migration failed', e);
            return null;
          }),
        };
      }
      await migrationRef.current.promise;

      const data = await listMyPatches();
      if (requestSeqRef.current !== seq) return;
      setRows(data);
      setStatus('ready');
    } catch (e) {
      if (requestSeqRef.current !== seq) return;
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [userId, sessionLoading]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const songs = useMemo<Song[]>(() => {
    if (anonymous) {
      // Anonymous: read-only seeded defaults. Favourites still apply
      // by song id so the home strip renders consistently if the user
      // had previously favourited a default and is now signed out.
      return DEFAULT_SONGS.map((s) => ({
        ...s,
        favourite: favourites.has(s.id),
      }));
    }
    return rows.map((r) => ({
      id: r.id,
      title: r.title,
      code: r.code,
      cloudPatchId: r.id,
      modifiedAt: new Date(r.updated_at).getTime(),
      favourite: favourites.has(r.id),
    }));
  }, [anonymous, rows, favourites]);

  const activeSong = useMemo(
    () => (activeId ? songs.find((s) => s.id === activeId) ?? null : null),
    [songs, activeId],
  );

  const select = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const requireAuthed = useCallback((op: string): string => {
    if (!userId) {
      throw new Error(`Sign in to ${op}.`);
    }
    return userId;
  }, [userId]);

  const insertSong = useCallback(
    (title: string, code: string): string => {
      // Synchronous return is required by the legacy contract (the
      // Learn module's "save scratch as song" flow expects an id).
      // Optimistically insert with a temporary id, then reconcile when
      // the cloud insert returns. This is the only place we mint
      // client-side ids; all other writes get ids from the server.
      const uid = requireAuthed('save songs');
      const supabase = getSupabase();
      if (!supabase) throw new Error('auth not configured');
      const tempId = `pending-${crypto.randomUUID()}`;
      const cleanTitle = (title.trim() || 'Untitled').slice(0, 200);
      const now = new Date().toISOString();
      const optimistic: CloudPatchRow = {
        id: tempId,
        title: cleanTitle,
        code,
        visibility: 'unlisted',
        created_at: now,
        updated_at: now,
      };
      setRows((prev) => [optimistic, ...prev]);
      setActiveId(tempId);

      void (async () => {
        const { data, error } = await supabase
          .from('patches')
          .insert({
            owner_id: uid,
            title: cleanTitle,
            code,
            visibility: 'unlisted',
          })
          .select('id, title, code, visibility, created_at, updated_at')
          .single();
        if (error) {
          // Roll back the optimistic row and surface the error.
          setRows((prev) => prev.filter((r) => r.id !== tempId));
          setActiveId((prev) => (prev === tempId ? null : prev));
          setStatus('error');
          setError(`save failed: ${error.message}`);
          return;
        }
        const real = data as CloudPatchRow;
        setRows((prev) => prev.map((r) => (r.id === tempId ? real : r)));
        setActiveId((prev) => (prev === tempId ? real.id : prev));
      })();

      return tempId;
    },
    [requireAuthed],
  );

  const save = useCallback(
    (title: string, code: string) => {
      const uid = requireAuthed('save');
      const cleanTitle = (title.trim() || 'Untitled').slice(0, 200);
      const supabase = getSupabase();
      if (!supabase) throw new Error('auth not configured');

      if (!activeId || activeId.startsWith('pending-')) {
        // No active id (or one still pending insert resolution) →
        // fall through to insertSong. The pending case is rare but
        // happens if the user mashes Save while the first insert is
        // in flight.
        insertSong(cleanTitle, code);
        return;
      }

      const previous = rows.find((r) => r.id === activeId);
      const now = new Date().toISOString();
      setRows((prev) =>
        prev.map((r) =>
          r.id === activeId ? { ...r, title: cleanTitle, code, updated_at: now } : r,
        ),
      );

      void (async () => {
        const { error } = await supabase
          .from('patches')
          .update({ title: cleanTitle, code })
          .eq('id', activeId)
          .eq('owner_id', uid);
        if (error) {
          // Roll back to the prior server-known state and surface.
          if (previous) {
            setRows((prev) => prev.map((r) => (r.id === activeId ? previous : r)));
          }
          setStatus('error');
          setError(`save failed: ${error.message}`);
        }
      })();
    },
    [activeId, rows, requireAuthed, insertSong],
  );

  const deleteSong = useCallback(
    (id: string) => {
      const uid = requireAuthed('delete songs');
      const supabase = getSupabase();
      if (!supabase) throw new Error('auth not configured');
      const previous = rows.find((r) => r.id === id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (activeId === id) setActiveId(null);

      void (async () => {
        if (id.startsWith('pending-')) return; // Optimistic-only row.
        const { error } = await supabase
          .from('patches')
          .delete()
          .eq('id', id)
          .eq('owner_id', uid);
        if (error) {
          if (previous) setRows((prev) => [previous, ...prev]);
          setStatus('error');
          setError(`delete failed: ${error.message}`);
        }
      })();
    },
    [activeId, rows, requireAuthed],
  );

  const prepareNew = useCallback(() => {
    setActiveId(null);
    return { title: 'New song', code: NEW_SONG_CODE };
  }, []);

  const importSongs = useCallback(
    (incoming: unknown): { added: number; updated: number } | null => {
      // Bulk-import from JSON. In cloud-first mode every imported
      // song becomes a fresh cloud INSERT — there is no "local id"
      // to update against. Returns counts in the same shape as the
      // legacy hook so existing UI copy (toasts) keeps working.
      requireAuthed('import songs');
      const supabase = getSupabase();
      if (!supabase) throw new Error('auth not configured');
      if (!Array.isArray(incoming)) return null;
      let added = 0;
      void (async () => {
        for (const raw of incoming) {
          if (typeof raw !== 'object' || raw === null) continue;
          const o = raw as Record<string, unknown>;
          if (typeof o.code !== 'string') continue;
          const title = typeof o.title === 'string' ? o.title : 'Imported song';
          const { error } = await supabase.from('patches').insert({
            owner_id: userId!,
            title: title.slice(0, 200),
            code: o.code,
            visibility: 'unlisted',
          });
          if (!error) added++;
        }
        await refetch();
      })();
      return { added, updated: 0 };
    },
    [refetch, requireAuthed, userId],
  );

  const resetToDefaults = useCallback((): Song | null => {
    requireAuthed('reset library');
    const supabase = getSupabase();
    if (!supabase) throw new Error('auth not configured');
    void (async () => {
      // Destructive: delete everything the user owns, then insert the
      // defaults. The DELETE WHERE owner_id is RLS-checked so we can't
      // blow away anyone else's patches.
      await supabase.from('patches').delete().eq('owner_id', userId!);
      for (const seed of DEFAULT_SONGS) {
        await supabase.from('patches').insert({
          owner_id: userId!,
          title: seed.title,
          code: seed.code,
          visibility: 'unlisted',
        });
      }
      await refetch();
    })();
    return DEFAULT_SONGS[0] ?? null;
  }, [refetch, requireAuthed, userId]);

  const clearAll = useCallback(() => {
    requireAuthed('clear library');
    const supabase = getSupabase();
    if (!supabase) throw new Error('auth not configured');
    setRows([]);
    setActiveId(null);
    void (async () => {
      const { error } = await supabase.from('patches').delete().eq('owner_id', userId!);
      if (error) {
        setStatus('error');
        setError(`clear failed: ${error.message}`);
        await refetch();
      }
    })();
  }, [refetch, requireAuthed, userId]);

  const setSongCloudPatchId = useCallback(
    (_songId: string, _cloudPatchId: string) => {
      // Cloud-first songs already have id === cloudPatchId. The legacy
      // hook used this to stamp a local-only song with its cloud
      // identity after publish; in cloud-first there's nothing to do.
    },
    [],
  );

  const upsertScratch = useCallback(
    (scratchId: string, title: string, code: string) => {
      // Learn's scratchpad. The caller passes a *stable* per-slot id
      // (e.g. "learn-scratch-1") and expects repeated calls to
      // overwrite the same row. Cloud-first ids are server-minted, so
      // we maintain a localStorage scratchId → cloudPatchId map and
      // route the second call as an UPDATE on the previously-created
      // row. Without this, every ▷ Try click would mint a fresh cloud
      // row and balloon the library.
      const uid = requireAuthed('save scratch');
      const supabase = getSupabase();
      if (!supabase) throw new Error('auth not configured');
      const cleanTitle = (title.trim() || 'Learn scratch').slice(0, 200);
      const map = loadScratchMap(uid);
      const mappedCloudId = map[scratchId];

      if (mappedCloudId && rows.some((r) => r.id === mappedCloudId)) {
        // Update path. Optimistic.
        const previous = rows.find((r) => r.id === mappedCloudId);
        const now = new Date().toISOString();
        setRows((prev) =>
          prev.map((r) =>
            r.id === mappedCloudId
              ? { ...r, title: cleanTitle, code, updated_at: now }
              : r,
          ),
        );
        setActiveId(mappedCloudId);
        void (async () => {
          const { error } = await supabase
            .from('patches')
            .update({ title: cleanTitle, code })
            .eq('id', mappedCloudId)
            .eq('owner_id', uid);
          if (error) {
            if (previous) {
              setRows((prev) =>
                prev.map((r) => (r.id === mappedCloudId ? previous : r)),
              );
            }
            setStatus('error');
            setError(`save scratch failed: ${error.message}`);
          }
        })();
        return;
      }

      // First-time path: insert, then record the mapping when the
      // server assigns the real cloud id.
      void (async () => {
        const { data, error } = await supabase
          .from('patches')
          .insert({
            owner_id: uid,
            title: cleanTitle,
            code,
            visibility: 'unlisted',
          })
          .select('id, title, code, visibility, created_at, updated_at')
          .single();
        if (error) {
          setStatus('error');
          setError(`save scratch failed: ${error.message}`);
          return;
        }
        const real = data as CloudPatchRow;
        setRows((prev) => [real, ...prev]);
        setActiveId(real.id);
        const next = { ...loadScratchMap(uid), [scratchId]: real.id };
        saveScratchMap(uid, next);
      })();
    },
    [requireAuthed, rows],
  );

  const toggleFavourite = useCallback(
    (id: string) => {
      // Anonymous users can flip the heart on the seeded defaults
      // locally; we store nothing because there's no cloud profile to
      // write to. The change is in-memory only and resets on reload.
      if (!session) {
        setFavourites((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        return;
      }
      setFavourites((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        // Fire and forget; on error we revert to whatever the next
        // session refresh tells us.
        void writeFavourites(next).catch((e) => {
          console.error('[library] favourite write failed', e);
        });
        return next;
      });
    },
    [session],
  );

  return {
    songs,
    activeId,
    activeSong,
    select,
    save,
    deleteSong,
    prepareNew,
    importSongs,
    resetToDefaults,
    clearAll,
    setSongCloudPatchId,
    insertSong,
    upsertScratch,
    toggleFavourite,
    status,
    error,
    refetch,
  };
}
